import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import {
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import { StarRating } from "@/components/reviews/StarRating";
import { format } from "date-fns";
import { AlertCircle, CheckCircle, XCircle, ArrowLeft, Star, Building2, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export default function ReviewsModeration() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [allReviews, setAllReviews] = useState<any[]>([]);
  const [filteredReviews, setFilteredReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filtri
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "approved" | "flagged">("all");
  const [responseFilter, setResponseFilter] = useState<"all" | "answered" | "unanswered">("all");
  const [recentFilter, setRecentFilter] = useState(false);

  useEffect(() => {
    loadReviews();
  }, []);

  useEffect(() => {
    filterReviews();
  }, [searchQuery, statusFilter, responseFilter, recentFilter, allReviews]);

  const loadReviews = async () => {
    try {
      const { data: reviewsData, error: reviewsError } = await supabase
        .from("reviews")
        .select(`
          *,
          profiles!reviews_profile_id_fkey (
            business_name,
            professional_email,
            slug
          ),
          companies!reviews_company_id_fkey (
            name,
            slug
          ),
          review_reports!review_reports_review_id_fkey (
            id,
            status,
            report_reason,
            report_details,
            created_at,
            reported_by
          )
        `)
        .order("created_at", { ascending: false });

      if (reviewsError) throw reviewsError;

      setAllReviews(reviewsData || []);
      setFilteredReviews(reviewsData || []);
    } catch (error) {
      console.error("Error loading reviews:", error);
      toast.error(t("admin.reviews.messages.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const filterReviews = () => {
    let filtered = allReviews;

    // Filtro per status report
    if (statusFilter === "pending") {
      filtered = filtered.filter(r => 
        r.review_reports && r.review_reports.some((report: any) => report.status === 'pending')
      );
    } else if (statusFilter === "approved") {
      filtered = filtered.filter(r => r.admin_approved === true);
    } else if (statusFilter === "flagged") {
      filtered = filtered.filter(r => r.flagged === true);
    }

    // Filtro per risposta
    if (responseFilter === "answered") {
      filtered = filtered.filter(r => r.response_text !== null && r.response_text !== "");
    } else if (responseFilter === "unanswered") {
      filtered = filtered.filter(r => !r.response_text);
    }

    // Filtro ultimi 24h
    if (recentFilter) {
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      filtered = filtered.filter(r => new Date(r.created_at) > oneDayAgo);
    }

    // Filtro ricerca per professionista o contenuto
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.profiles?.business_name?.toLowerCase().includes(query) ||
        r.title?.toLowerCase().includes(query) ||
        r.comment?.toLowerCase().includes(query)
      );
    }

    setFilteredReviews(filtered);
  };

  const handleApprove = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from("reviews")
        .update({ admin_approved: true, flagged: false })
        .eq("id", reviewId);

      if (error) throw error;
      toast.success(t("admin.reviews.messages.approved"));
      loadReviews();
    } catch (error) {
      console.error("Error approving review:", error);
      toast.error(t("admin.reviews.messages.approveError"));
    }
  };

  const handleToggleFlag = async (reviewId: string, currentFlagged: boolean) => {
    try {
      const { error } = await supabase
        .from("reviews")
        .update({ flagged: !currentFlagged })
        .eq("id", reviewId);

      if (error) throw error;
      toast.success(currentFlagged ? t("admin.reviews.messages.flagRemoved") : t("admin.reviews.messages.flagged"));
      loadReviews();
    } catch (error) {
      console.error("Error toggling flag:", error);
      toast.error(t("admin.reviews.messages.flagError"));
    }
  };

  const handleDismissReports = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from("review_reports")
        .update({ status: "dismissed", reviewed_at: new Date().toISOString() })
        .eq("review_id", reviewId)
        .eq("status", "pending");

      if (error) throw error;
      toast.success(t("admin.reviews.messages.reportsArchived"));
      loadReviews();
    } catch (error) {
      console.error("Error dismissing reports:", error);
      toast.error(t("admin.reviews.messages.archiveError"));
    }
  };

  const handleDelete = async (reviewId: string) => {
    if (!confirm(t("admin.reviews.dialog.deleteConfirm"))) return;

    try {
      const { error } = await supabase
        .from("reviews")
        .delete()
        .eq("id", reviewId);

      if (error) throw error;
      toast.success(t("admin.reviews.messages.deleted"));
      loadReviews();
    } catch (error) {
      console.error("Error deleting review:", error);
      toast.error(t("admin.reviews.messages.deleteError"));
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.reviews.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.reviews.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <Star className="w-10 h-10 text-warning" />
              {t("admin.reviews.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("admin.reviews.subtitle")}</p>
          </div>
        </div>

        {/* Sezione Filtri */}
        <BrutalCard className="p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Ricerca */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-bold mb-2">{t("admin.reviews.filters.searchLabel")}</label>
              <BrutalInput
                type="text"
                placeholder={t("admin.reviews.filters.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Filtro Status */}
            <div>
              <label className="block text-sm font-bold mb-2">{t("admin.reviews.filters.statusLabel")}</label>
              <BrutalSelect value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
                <BrutalSelectTrigger>
                  <BrutalSelectValue placeholder={t("admin.reviews.filters.statusPlaceholder")} />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  <BrutalSelectItem value="all">{t("admin.reviews.filters.allStatuses")}</BrutalSelectItem>
                  <BrutalSelectItem value="pending">{t("admin.reviews.filters.pending")}</BrutalSelectItem>
                  <BrutalSelectItem value="approved">{t("admin.reviews.filters.approved")}</BrutalSelectItem>
                  <BrutalSelectItem value="flagged">{t("admin.reviews.filters.flagged")}</BrutalSelectItem>
                </BrutalSelectContent>
              </BrutalSelect>
            </div>

            {/* Filtro Risposta */}
            <div>
              <label className="block text-sm font-bold mb-2">{t("admin.reviews.filters.responseLabel")}</label>
              <BrutalSelect value={responseFilter} onValueChange={(value: any) => setResponseFilter(value)}>
                <BrutalSelectTrigger>
                  <BrutalSelectValue placeholder={t("admin.reviews.filters.responsePlaceholder")} />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  <BrutalSelectItem value="all">{t("admin.reviews.filters.allResponses")}</BrutalSelectItem>
                  <BrutalSelectItem value="answered">{t("admin.reviews.filters.answered")}</BrutalSelectItem>
                  <BrutalSelectItem value="unanswered">{t("admin.reviews.filters.unanswered")}</BrutalSelectItem>
                </BrutalSelectContent>
              </BrutalSelect>
            </div>
          </div>

          {/* Checkbox Ultimi 24h */}
          <div className="mt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={recentFilter}
                onChange={(e) => setRecentFilter(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="font-heading font-bold text-sm">{t("admin.reviews.filters.recentOnly")}</span>
            </label>
          </div>

          {/* Contatore risultati */}
          <div className="mt-4 pt-4 border-t-[2px] border-dark">
            <p className="text-sm font-bold">
              {t("admin.reviews.filters.showingResults", { filtered: filteredReviews.length, total: allReviews.length })}
            </p>
          </div>
        </BrutalCard>

        {filteredReviews.length === 0 ? (
          <BrutalCard className="p-12 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-success" />
            <p className="text-muted-foreground">
              {searchQuery || statusFilter !== 'all' || responseFilter !== 'all' || recentFilter
                ? t("admin.reviews.emptyState.noResults")
                : t("admin.reviews.emptyState.noReviews")}
            </p>
          </BrutalCard>
        ) : (
          <div className="space-y-6">
            {filteredReviews.map((review) => {
              const hasActiveReports = review.review_reports && 
                review.review_reports.some((r: any) => r.status === 'pending');
              
              return (
                <BrutalCard key={review.id} className="p-6">
                  {/* Header con Badge Status */}
                  <div className="flex items-start justify-between mb-4 pb-4 border-b-[2px] border-dark">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {/* Review Type Badge */}
                        {review.company_id ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold border-[2px] border-blue-700">
                            <Building2 className="w-3 h-3" />
                            {t("reviews.companyReview", "Azienda")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold border-[2px] border-amber-700">
                            <User className="w-3 h-3" />
                            {t("reviews.profileReview", "Profilo")}
                          </span>
                        )}
                        <span className="font-bold text-lg">
                          {review.company_id 
                            ? review.companies?.name 
                            : review.profiles?.business_name || t("admin.reviews.card.unknownProfessional")}
                        </span>
                        {hasActiveReports && (
                          <BrutalBadge variant="warning">{t("admin.reviews.badges.reported")}</BrutalBadge>
                        )}
                        {review.flagged && (
                          <BrutalBadge variant="warning">{t("admin.reviews.badges.flagged")}</BrutalBadge>
                        )}
                        {!review.admin_approved && (
                          <BrutalBadge variant="secondary">{t("admin.reviews.badges.notApproved")}</BrutalBadge>
                        )}
                      </div>
                      <StarRating rating={review.rating} readonly size="sm" />
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(review.created_at), "dd MMM yyyy HH:mm")}
                      </p>
                    </div>
                  </div>

                  {/* Contenuto Recensione */}
                  <div className="mb-4">
                    <h3 className="font-heading text-lg font-bold mb-2">
                      {review.title}
                    </h3>
                    <p className="text-foreground mb-2">{review.comment}</p>
                    
                    {/* Risposta professionista */}
                    {review.response_text && (
                      <div className="mt-3 p-3 bg-accent/50 border-l-4 border-primary">
                        <p className="text-sm font-bold mb-1">{t("admin.reviews.card.professionalResponse")}</p>
                        <p className="text-sm">{review.response_text}</p>
                      </div>
                    )}
                  </div>

                  {/* Segnalazioni attive */}
                  {hasActiveReports && (
                    <div className="mb-4 p-3 bg-warning/10 border-[2px] border-warning">
                      <p className="font-bold text-sm mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {t("admin.reviews.card.activeReports")}
                      </p>
                      {review.review_reports
                        .filter((r: any) => r.status === 'pending')
                        .map((report: any) => (
                          <div key={report.id} className="text-xs mb-2 pb-2 border-b border-warning/30 last:border-0 last:pb-0 last:mb-0">
                            <div className="font-semibold">• {t(`reports.reasons.${report.report_reason}`, report.report_reason) as string}</div>
                            {report.report_details && (
                              <div className="mt-1 text-muted-foreground italic pl-3">
                                "{report.report_details}"
                              </div>
                            )}
                            <div className="text-muted-foreground pl-3 mt-1">
                              {format(new Date(report.created_at), "dd/MM/yyyy HH:mm")}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 flex-wrap">
                    {!review.admin_approved && (
                      <BrutalButton
                        variant="outline"
                        size="sm"
                        onClick={() => handleApprove(review.id)}
                        className="border-success text-success hover:bg-success hover:text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {t("admin.reviews.buttons.approve")}
                      </BrutalButton>
                    )}
                    
                    <BrutalButton
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleFlag(review.id, review.flagged)}
                      className={review.flagged 
                        ? "border-success text-success hover:bg-success hover:text-white"
                        : "border-warning text-warning hover:bg-warning hover:text-white"}
                    >
                      {review.flagged ? t("admin.reviews.buttons.removeFlag") : t("admin.reviews.buttons.flag")}
                    </BrutalButton>

                    {hasActiveReports && (
                      <BrutalButton
                        variant="outline"
                        size="sm"
                        onClick={() => handleDismissReports(review.id)}
                      >
                        {t("admin.reviews.buttons.archiveReports")}
                      </BrutalButton>
                    )}

                    <BrutalButton
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(review.id)}
                      className="border-destructive text-destructive hover:bg-destructive hover:text-white"
                    >
                        <XCircle className="w-4 h-4 mr-2" />
                        {t("admin.reviews.buttons.delete")}
                      </BrutalButton>
                  </div>
                </BrutalCard>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
